import { Guid } from "client_legacy/ndaw/guid";
import {
	ReactElement,
	useEffect,
	useMemo,
	useState
} from "react";
import { InterestedMagisterParty, MagisterLiveInformationProvider } from "./provider";
import { ScheduleKind } from "./Schedule";
import {
	MagisterAppointment,
	ResultStatus,
	ScheduleResponseModel,
	StatusKind
} from "client/Api/Workspace/LiveInformation/Magister/magisterLiveInformation";
import styles from "./WeekSchedule.module.less";
import {
	BusyIndicator,
	SliderWithToggle,
	WeekSlider,
	WeekView
} from "client/Components";
import { TileRefresh } from "../TileRefresh/TileRefresh";
import { LiveTileContent } from "../LiveTileContent";
import { localize } from "client_legacy/ndaw/culture";
import {
	useWeekDays,
	useWeekScheduleBlocks,
	WeekDay
} from "client/Hooks";
import { AppointmentCard } from "./AppointmentCard";
import { TimeZone } from "client/DateAndTime/timezones";
import { WeekMode } from "client/Misc/commonTypes";

export type WeekScheduleProps = {
	tabId: Guid;
	connectionId: number;
	provider: MagisterLiveInformationProvider;
	date: Date;
	onDateChange(date: Date): void;
	onChangeScheduleKind(kind: ScheduleKind): void;
	isTodayButtonAvailable?: boolean;
	onNavigate(): void;
	timezone: TimeZone;
};

const weekMode: WeekMode = "culture";

export const WeekSchedule = (props: WeekScheduleProps): ReactElement => {
	const {
		tabId,
		connectionId,
		provider,
		date,
		onDateChange,
		onChangeScheduleKind,
		isTodayButtonAvailable,
		onNavigate,
		timezone
	} = props;

	const [loadingState, setLoadingState] = useState<LoadingState>({
		isLoading: true
	});

	const days: readonly WeekDay[] = useWeekDays(
		date,
		weekMode,
		timezone
	);

	if (days.length === 0) {
		throw new Error("No days in the week.");
	}

	const startOfWeek: Date = days[0].date;

	useEffect(
		(): (() => void) => {
			const party: InterestedMagisterParty = {
				partyId: tabId,
				resourceId: {
					connectionId,
					date: startOfWeek
				},
				informAboutGlobalRetrievalFailure: (): void => {
					setLoadingState({
						isLoading: false,
						hasGlobalFailure: true
					});
				},
				informAboutConsecutiveErrors: (): void => {
					setLoadingState({
						isLoading: false,
						hasTooManyConsecutiveErrors: true
					});
				},
				updateWeekSchedule: (response: ScheduleResponseModel, isTeacher: boolean | null): void => {
					setLoadingState((x) => ({
						...x,
						isLoading: false,
						hasTooManyConsecutiveErrors: false,
						hasGlobalFailure: false,
						isTeacher,
						response
					}));
				}
			};

			setLoadingState((x) => ({
				...x,
				isLoading: true
			}));

			provider.register(party, { weekSchedule: true });

			return () => {
				provider.unregister(party);
			};
		},
		[
			connectionId,
			provider,
			tabId,
			startOfWeek
		]
	);

	const tileRefreshHandler = (): void => {
		setLoadingState((x) => ({
			...x,
			isLoading: true
		}));
		provider.forceReload();
	};

	const items = useMemo(
		(): readonly MagisterAppointment[] => {
			return !loadingState.isLoading
				&& !loadingState.hasGlobalFailure
				&& !loadingState.hasTooManyConsecutiveErrors
				&& loadingState.response.status === ResultStatus.Success
					? loadingState.response
						.items
						// #37492 for now the WeekView does not support overlapping appointments
						// so we decided to don't render cancelled appointments until the WeekView
						// does support overlapping
						.filter(x => x.status !== StatusKind.Cancelled)
					: [];
		},
		[loadingState]
	);

	const {
		startHour,
		endHour,
		blocksPerDay
	} = useWeekScheduleBlocks(
		items,
		startDateTimeRetriever,
		endDateTimeRetriever,
		timezone
	);

	const renderContent = (): ReactElement => {
		if (loadingState.isLoading) {
			return <BusyIndicator />;
		}

		if (loadingState.hasTooManyConsecutiveErrors) {
			return <TileRefresh onRefresh={tileRefreshHandler} />;
		}

		if (loadingState.hasGlobalFailure
			|| loadingState.response.status !== ResultStatus.Success
		) {
			return (
				<LiveTileContent.NoResults
					icon="gear"
					title={localize("_UnableToRetrieveItems")}
					message={localize("_WeWereUnableToRetrieveTheItems")}
					onRetry={tileRefreshHandler}
					trackingId="has-error"
				/>
			);
		}

		const { response } = loadingState;

		if (response.items.length === 0) {
			return (
				<LiveTileContent.NoResults
					icon="calendar"
					title={localize("_NoClassesThisWeek")}
					message={localize("_YouDontHaveAnyClassesThisWeek")}
					trackingId="no-items"
				/>
			);
		}

		return (
			<LiveTileContent.Scrollable>
				<WeekView
					mode="hours"
					startHour={startHour}
					endHour={endHour}
					timezone={timezone}
				>
					{days.map((x) => (
						<WeekView.Day
							key={x.date.toISOString()}
							title={x.name}
							isToday={x.isToday}
							dayInTheMonth={x.dayInTheMonth}
						>
							{(blocksPerDay[x.day] ?? []).map((x) => (
								<WeekView.TimeBlock
									key={x.startTimeInMinutes}
									durationInMinutes={x.durationInMinutes}
								>
									{x.appointment && (
										<AppointmentCard
											appointment={x.appointment}
											onNavigate={onNavigate}
											timezone={timezone}
										/>
									)}
								</WeekView.TimeBlock>
							))}
						</WeekView.Day>
					))}
				</WeekView>
			</LiveTileContent.Scrollable>
		);
	};

	return (
		<>
			<div className={styles.sliderContainer}>
				<SliderWithToggle>
					<SliderWithToggle.Slider>
						<WeekSlider
							startDate={startOfWeek}
							onChange={onDateChange}
							isTodayButtonAvailable={isTodayButtonAvailable}
							mode={weekMode}
							timezone={timezone}
						/>
					</SliderWithToggle.Slider>
					<SliderWithToggle.Toggle
						icon="calendar-day"
						onClick={() => onChangeScheduleKind(ScheduleKind.Day)}
					/>
				</SliderWithToggle>
			</div>
			{renderContent()}
		</>
	);
};
WeekSchedule.displayName = "WeekSchedule";

type LoadingState = {
	isLoading: true;
} | {
	isLoading: false;
} & (
		{
			hasTooManyConsecutiveErrors: true;
			hasGlobalFailure?: undefined;
		}
		|
		{
			hasGlobalFailure: true;
			hasTooManyConsecutiveErrors?: undefined;
		}
		|
		{
			hasTooManyConsecutiveErrors: false;
			hasGlobalFailure: false;
			isTeacher: boolean | null;
			response: ScheduleResponseModel;
		}
	);

function startDateTimeRetriever(appointment: MagisterAppointment): string {
	return appointment.startDateTime;
}

function endDateTimeRetriever(appointment: MagisterAppointment): string {
	return appointment.finishDateTime;
}
